import { Button, Tag } from 'antd';
import { ApartmentOutlined, ContactsOutlined, PlusOutlined, ShoppingCartOutlined, UserOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serverFetch } from '@server';
import { showError, fmtDate, fmtMoney } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import { useUrlFilters } from '@extensions/url-filters';
import { PageTitle } from '@extensions/layout';
import FilterBar from '@controls/filter-bar/filter-bar';
import CardList, { CardRow } from '@controls/card-list/card-list';
import Order from './order';
import Contragent from '../contragents/contragent';
import { LogoSlot } from '../contragents/contragent-logo';

// Фильтры в URL: ?p1=&p2=&q=&user=&status=&tag=&debt=1
const FILTER_SPEC = {
    period: { param: 'p', type: 'range', empty: null },
    filter: { param: 'q', type: 'str', empty: '' },
    userId: { param: 'user', type: 'int', empty: 0 },
    statusId: { param: 'status', type: 'int', empty: 0 },
    tag: { param: 'tag', type: 'int', empty: 0 },
    onlyDebt: { param: 'debt', type: 'bool', empty: false },
};

const Orders = () => {
    const { dicts } = useDicts();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilter] = useUrlFilters(FILTER_SPEC, 'orderFilters');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editOrder, setEditOrder] = useState(null); // 0 — новый, id — редактирование
    const [editContragent, setEditContragent] = useState(null);

    const period = filters.period;

    const load = useCallback(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (period?.[0]) qs.append('begPer', period[0]);
        if (period?.[1]) qs.append('endPer', period[1]);
        if (filters.filter) qs.append('filter', filters.filter);
        if (filters.userId) qs.append('userId', filters.userId);
        if (filters.statusId) qs.append('statusId', filters.statusId);
        if (filters.tag) qs.append('tag', filters.tag);
        if (filters.onlyDebt) qs.append('onlyDebt', 'true');
        serverFetch(`order?${qs.toString()}`)
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, [filters, period]);

    useEffect(() => { load(); }, [load]);

    // Открыть карточку по ?id=
    useEffect(() => {
        const id = Number(searchParams.get('id'));
        if (id > 0) setEditOrder(id);
    }, [searchParams]);

    const closeOrder = () => {
        setEditOrder(null);
        if (searchParams.get('id')) {
            searchParams.delete('id');
            setSearchParams(searchParams, { replace: true });
        }
        load();
    };

    const onDelete = (record) => {
        serverFetch(`order/${record.id}`, { method: 'DELETE' })
            .then(load)
            .catch(showError);
    };

    const filterDefs = useMemo(() => [
        { key: 'period', label: 'Период', type: 'range', emptyValue: null },
        { key: 'onlyDebt', label: 'Долг', emptyValue: false, options: [{ value: true, label: 'только с долгом' }] },
        { key: 'userId', label: 'Исполнитель', emptyValue: 0, options: dicts.users.map((u) => ({ value: u.id, label: u.name })) },
        { key: 'statusId', label: 'Статус', emptyValue: 0, options: dicts.orderStatuses.map((s) => ({ value: s.id, label: s.name })) },
        {
            key: 'tag', label: 'Метка', emptyValue: 0,
            options: dicts.tags.filter((t) => t.vid === 5).map((t) => ({ value: t.id, label: t.name })),
        },
    ], [dicts.users, dicts.orderStatuses, dicts.tags]);

    const tagById = useMemo(() => new Map(dicts.tags.map((t) => [t.id, t])), [dicts.tags]);
    const statusById = useMemo(() => new Map(dicts.orderStatuses.map((s) => [s.id, s])), [dicts.orderStatuses]);

    const totals = useMemo(() => items.reduce(
        (acc, o) => ({
            syma: acc.syma + Number(o.syma ?? 0),
            payment: acc.payment + Number(o.payment ?? 0),
            dolg: acc.dolg + Number(o.dolg ?? 0),
        }),
        { syma: 0, payment: 0, dolg: 0 },
    ), [items]);

    return (
        <div>
            <div className="page-toolbar">
                <PageTitle>Заказы</PageTitle>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditOrder(0)}>Добавить</Button>
                <FilterBar
                    filters={filterDefs}
                    value={filters}
                    onChange={setFilter}
                    searchValue={filters.filter}
                    onSearch={(v) => setFilter({ filter: v })}
                    placeholder="Поиск заказов"
                />
                <span className="page-toolbar-info">
                    <span>Сумма {fmtMoney(totals.syma)}</span>
                    {' / '}
                    <span style={{ color: 'green' }}>Оплата {fmtMoney(totals.payment)}</span>
                    {' / '}
                    <span style={{ color: 'red' }}>Долг {fmtMoney(totals.dolg)}</span>
                </span>
            </div>
            <CardList
                items={items}
                loading={loading}
                pageSize={30}
                emptyText="Заказов нет"
                deleteTitle="Удалить заказ?"
                onClick={(r) => setEditOrder(r.id)}
                onDelete={onDelete}
                renderAside={(r) => (r.contragentId
                    ? <LogoSlot fileId={r.contragentLogoFileId} name={r.contragentLogoName || r.contragentName} />
                    : null)}
                renderTitle={(r) => {
                    const st = statusById.get(r.statusId);
                    return (
                        <>
                            {!r.contragentId && <ShoppingCartOutlined />}
                            <span>{r.docNum ? `Заказ №${r.docNum}` : `Заказ #${r.id}`}</span>
                            {r.docNum ? <span className="card-list-muted">#{r.id}</span> : null}
                            <span className="card-list-muted">{fmtDate(r.docDate)}</span>
                            {r.statusName && <Tag color={st?.color || undefined}>{r.statusName}</Tag>}
                            {(r.tags ?? []).map((id) => {
                                const t = tagById.get(id);
                                return t ? (
                                    <Tag key={id} color={t.bgColor || undefined} style={{ color: t.textColor || undefined }}>{t.name}</Tag>
                                ) : null;
                            })}
                            <span className="card-list-sums">
                                <span className="card-list-muted">Сумма </span>{fmtMoney(r.syma)}
                                <span className="card-list-muted" style={{ marginLeft: 12 }}>Оплата </span>{fmtMoney(r.payment)}
                                {r.paymentDate && <span className="card-list-muted"> ({fmtDate(r.paymentDate)})</span>}
                                <span className="card-list-muted" style={{ marginLeft: 12 }}>Долг </span>
                                <span style={Number(r.dolg) > 0 ? { color: 'red' } : undefined}>{fmtMoney(r.dolg)}</span>
                            </span>
                        </>
                    );
                }}
                renderBody={(r) => (
                    <>
                        {r.contragentId && (
                            <CardRow icon={<ContactsOutlined />}>
                                <Button
                                    type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                    onClick={(e) => { e.stopPropagation(); setEditContragent(r.contragentId); }}
                                >
                                    {r.contragentName || `#${r.contragentId}`}
                                </Button>
                            </CardRow>
                        )}
                        <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>
                        <CardRow icon={<ApartmentOutlined />} title="Отдел">{r.departmentName}</CardRow>
                        <CardRow className="card-list-muted card-list-wide">{r.comment}</CardRow>
                    </>
                )}
            />
            {editOrder !== null && (
                <Order
                    id={editOrder}
                    onClose={closeOrder}
                    onSaved={load}
                    onOpenContragent={(id) => setEditContragent(id)}
                />
            )}
            {editContragent !== null && (
                <Contragent id={editContragent} onClose={() => setEditContragent(null)} />
            )}
        </div>
    );
};

export default Orders;
